import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, Paper, Stack, TextField, Typography } from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { OriginalsView } from '../../types/pettyCash';
import { formatDate } from '../../utils/pettyCash';

/**
 * The original paper receipts: finance marks them received (with the transmittal they came with), or removes the
 * mark. The setup can require them before the last finance approval.
 */

interface CardProps {
  originals: OriginalsView;
  canMark: boolean;
  busy: boolean;
  onMark: () => void;
  onRemove: () => void;
}

export const OriginalsCard: React.FC<CardProps> = ({ originals: o, canMark, busy, onMark, onRemove }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Original receipts</Typography>
    <Stack spacing={0.75} alignItems="flex-start">
      {o.receivedAt ? (
        <>
          <Typography variant="body2">
            <strong>Received</strong> {formatDate(o.receivedAt)}{o.reference ? ` · transmittal ${o.reference}` : ''}
          </Typography>
          {o.receivedByName ? <Typography variant="caption" color="text.secondary">Marked by {o.receivedByName}</Typography> : null}
          {o.note ? <Typography variant="body2" color="text.secondary">{o.note}</Typography> : null}
        </>
      ) : (
        <Typography variant="body2" color={o.required ? 'warning.dark' : 'text.secondary'} fontWeight={o.required ? 600 : 400}>Not received</Typography>
      )}
      {o.required ? <Typography variant="caption" color="text.secondary">Required before the last finance approval</Typography> : null}
      {canMark ? (
        <Button size="small" variant={o.receivedAt ? 'outlined' : 'contained'} onClick={o.receivedAt ? onRemove : onMark} disabled={busy}>
          {o.receivedAt ? 'Remove mark' : 'Mark originals received'}
        </Button>
      ) : null}
    </Stack>
  </Paper>
);

interface DialogProps {
  open: boolean;
  /** mark: the originals arrived; remove: take the mark away. */
  mode: 'mark' | 'remove';
  onClose: () => void;
  onSave: (input: { received: boolean; reference: string | null; note: string | null }) => Promise<void>;
}

export const OriginalsDialog: React.FC<DialogProps> = ({ open, mode, onClose, onSave }) => {
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReference('');
    setNote('');
    setError(null);
    setBusy(false);
  }, [open, mode]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ received: mode === 'mark', reference: mode === 'mark' ? reference.trim() || null : null, note: note.trim() || null });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The original receipts could not be marked');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby="pc-originals-title">
      <DialogTitle id="pc-originals-title">{mode === 'mark' ? 'Original receipts received' : 'Remove the mark?'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {mode === 'mark'
              ? 'The paper receipts of this claim reached finance.'
              : 'The claim shows its original receipts as not received again.'}
          </Typography>
          {mode === 'mark' ? (
            <TextField
              id="pc-originals-reference"
              label="Transmittal no."
              value={reference}
              onChange={(e) => setReference(e.target.value.slice(0, 100))}
              helperText="The cover sheet or envelope they came with (optional)"
              fullWidth
            />
          ) : null}
          <TextField id="pc-originals-note" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} multiline minRows={2} fullWidth />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Back</Button>
        <Button color={mode === 'remove' ? 'error' : 'primary'} onClick={() => { void save(); }} disabled={busy}>
          {busy ? 'Saving…' : mode === 'mark' ? 'Mark received' : 'Remove mark'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};
